import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Small fixed-position badge showing the build commit hash and time.
 * Lets us instantly verify whether a user is on the latest deployed assets
 * (vs. a stale service-worker cached version).
 *
 * Includes an "Actualiser maintenant" button that purges local caches
 * (Service Workers + Cache Storage) and forces a clean reload.
 */
export function BuildIndicator() {
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const commit = typeof __BUILD_COMMIT__ !== "undefined" ? __BUILD_COMMIT__ : "dev";
  const time = typeof __BUILD_TIME__ !== "undefined" ? __BUILD_TIME__ : "";

  const formatted = time
    ? new Date(time).toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  async function handleHardRefresh(e: React.MouseEvent) {
    e.stopPropagation();
    if (refreshing) return;
    setRefreshing(true);
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
      }
      try {
        sessionStorage.removeItem("pwa:purged");
        sessionStorage.removeItem("app:reloaded-for-version");
      } catch { /* noop */ }
    } finally {
      const url = new URL(window.location.href);
      url.searchParams.set("_r", Date.now().toString());
      window.location.replace(url.toString());
    }
  }

  return (
    <div
      className={cn(
        "fixed bottom-2 right-2 z-[60] flex items-center gap-1 select-none rounded-md border border-border/60",
        "bg-card/80 backdrop-blur px-1.5 py-1 text-[10px] font-mono text-muted-foreground shadow-sm",
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        title={`Build ${commit} • ${time}`}
        className="flex items-center hover:text-foreground transition-colors px-1"
      >
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-success mr-1.5 align-middle" />
        build {commit}
        {expanded && formatted && <span className="ml-2 opacity-70">{formatted}</span>}
      </button>
      <button
        type="button"
        onClick={handleHardRefresh}
        disabled={refreshing}
        title="Actualiser maintenant (purge cache + reload)"
        className="flex items-center justify-center rounded p-1 hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
        aria-label="Actualiser maintenant"
      >
        <RefreshCw className={cn("h-3 w-3", refreshing && "animate-spin")} />
      </button>
    </div>
  );
}
